import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, TextField, FormControlLabel, Checkbox, RadioGroup, Radio, Switch, Typography, Box, Tabs } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import * as Base64Binary from 'base64-arraybuffer';
import CancelIcon from '@material-ui/icons/Cancel';
import { green } from '@material-ui/core/colors';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { getExtension } from '../../helpers/helper';
import 'draft-js-prism-decorator';
import 'prismjs/components/prism-typescript';
import 'prismjs/themes/prism.css';

export function Dropdown({ action, inputName, options, multiple, isRequired, setField, fullWidth = true, setRefresh, refresh = undefined }) {
    const { t } = useTranslation();
    const [dropdownValue, setDropdownValue] = useState("");

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { isRequired };
        }
        if (action.fields[inputName].value) {
            setDropdownValue(action.fields[inputName].value);
        } else {
            action.fields[inputName].value = options.defaultValue;
            setDropdownValue(options.defaultValue);
        }
    }, [action, refresh]);

    const handleChangeState = (event) => {
        if (refresh !== null && refresh !== undefined) {
            setRefresh(!refresh);
        }
        setDropdownValue(event.target.value);
        action.fields[inputName].value = event.target.value;
        if (setField) {
            setField(inputName, { value: event.target.value });
        }
    };

    return (
        <>
            {/* <div>{(isRequired === true) && <small style={{color: '#D75E5E'}}>* Required</small>}</div> */}
            <Select value={dropdownValue} onChange={handleChangeState} variant="outlined"
                multiple={multiple} fullWidth={fullWidth} style={{ height: 34, fontSize: 12, color: "#8F94A6", fontWeight: "800", borderRadius: 2 }}>
                {options.data.map((item, itemI) =>
                    <MenuItem style={{ fontSize: 12, color: "#8F94A6", fontWeight: "800" }} key={itemI} value={item.value}>{t(item.label)}</MenuItem>
                )}
            </Select>
        </>
    );
}

export function FileInput({ action, inputName, options, isRequired, clear, disabled, isSize = false }) {
    const [inputValue, setInputValue] = useState('');
    const { t } = useTranslation();

    useEffect(() => {
        if (action.fields) {
            if (!action.fields[inputName]) {
                action.fields[inputName] = { type: 'FILE', isRequired };
            }
            if (action.fields[inputName].value) {
                setInputValue(action.fields[inputName].value);
            } else {
                setInputValue('');
            }
        } else {
            if (!action[inputName]) {
                action[inputName] = { type: 'FILE', isRequired };
            }
            if (action[inputName].value) {
                setInputValue(action[inputName].value);
            } else {
                setInputValue('');
            }
        }
    }, [action]);

    const handleChange = (event) => {
        let ext = getExtension(event.target.value);
        if (options?.checkExtension) {
            if (!options.checkExtension(ext)) {
                return false;
            }
        }
        const reader = new FileReader();
        reader.readAsArrayBuffer(event.target.files[0]);

        reader.onload = (e) => {
            const arrayBuffer = new Uint8Array(e.target.result);
            const base64convert = Base64Binary.encode(arrayBuffer);
            if (action.fields) {
                action.fields[inputName].byteValue = base64convert;
            } else {
                action[inputName].byteValue = base64convert;
            }
        };

        const fullName = event.target.files[0].name;
        if (action.fields) {
            action.fields[inputName].fileName = fullName.split('.').slice(0, -1).join('.');
            action.fields[inputName].extension = ext;
            action.fields[inputName].value = fullName;
        } else {
            action[inputName].fileName = fullName.split('.').slice(0, -1).join('.');
            action[inputName].extension = ext;
            action[inputName].value = fullName;
        }
        //If the "isSize" field is true, fileSize key and value add it
        if (isSize) {
            if (action.fields) {
                action.fields[inputName].fileSize = { value: event.target.files[0].size };
            } else {
                action[inputName].fileSize = { value: event.target.files[0].size };
            }
        }
        setInputValue(fullName);
    };

    const handleClear = () => {
        if (action.fields) {
            delete action.fields[inputName].value;
        } else {
            delete action[inputName].value;
        }
        setInputValue(undefined);
    };

    return (
        <>
            <div>{(isRequired && inputValue.length === 0) && <small style={{ color: '#D75E5E' }}>{t('FLOW-MESSAGES.LIBRARY.GENERAL.REQUIRED')}</small>}</div>
            <div>
                <div className="custom-file config-form" style={{ marginTop: 10, width: '100%' }}>
                    <input type="file" className="custom-file-input" accept={options?.fileType} disabled={disabled} id="license-customFile" onClick={(event) => event.target.value = ''} onChange={handleChange} />
                    <label className="custom-file-label content-overflow" style={{ maxWidth: '100%' }} htmlFor="license-customFile">{inputValue ? inputValue : t('CONTENT.SETTINGS.GENERAL.CHOOSE')}</label>
                </div>
                {(clear && inputValue) &&
                    <CancelIcon onClick={handleClear} style={{ float: 'right', cursor: 'pointer' }} />}
            </div>
        </>
    );
}

export function Checkboxes({ action, inputName, options, onChange, isRequired, style }) {
    const [state, setState] = useState(false);
    const { t } = useTranslation();

    const handleChange = (event) => {
        setState(event.target.checked);
        action.fields[inputName].value = event.target.checked;
        if (onChange) {
            onChange({ value: event.target.checked });
        }
    };

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { value: false };
        }
        setState(action.fields[inputName].value);
    }, [action]);

    return (
        <FormControlLabel
            control={<Checkbox checked={state} size="small" color="#C4C4C4" onChange={handleChange} />}
            label={<Typography style={style ? style : { fontSize: "12px", fontWeight: 700, color: "#C4C4C4" }}
                variant="body2">{t(options.label)}</Typography>}
        />
    );
}

export function RadioButtons({ action, inputName, options, isRequired, setField }) {
    const [value, setValue] = useState();
    const { t } = useTranslation();

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { type: 'RADIO', value: options.defaultValue, isRequired };
            if (setField) {
                setField(inputName, { type: 'RADIO', value: options.defaultValue, isRequired });
            }
        }
        setValue(action.fields[inputName].value);
    }, [action]);

    const handleChange = (event) => {
        setValue(event.target.value);
        action.fields[inputName].value = event.target.value;
        if (setField) {
            setField(inputName, { type: 'RADIO', value: event.target.value, isRequired });
        }
    };

    return (
        <RadioGroup value={value ? value : options.defaultValue} onChange={handleChange}>
            {options.data.map((item, itemI) =>
                <FormControlLabel
                    key={itemI} value={item.value}
                    control={<Radio color="#C4C4C4" />}
                    label={t(item.label)}
                    labelPlacement="end"
                    size="small"
                />
            )}
        </RadioGroup>
    );
}

export function SwitchElement({ item }) {
    return (
        <FormControlLabel
            label={item?.value?.value?.value_labels}
            control={<Switch
                defaultChecked={item?.value?.value?.selected}
                // checked={state.checkedB}
                // onChange={handleChange('checkedB')}
                value="checkedB" color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
            />}
        />
    );
}

export const DateTime = (({ action, inputName, personalization, type = 'date', isRequired }) => {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { value: '', isRequired };
        } else {
            setValue(action.fields[inputName].value);
        }
    }, [action]);

    const handleChange = (event) => {
        setValue(event.target.value);
        action.fields[inputName].value = event.target.value; //.toLocaleString();
    };
    return (
        <TextField value={value}
            format={`${personalization?.date_format} HH:mm`}
            fullWidth type={type}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
        />
    );
});

export function TextInput({ action, inputName, type = 'number', variant, options }) {
    let defaultValue = options?.defaultValue ? options.defaultValue : '';
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { value: defaultValue };
        } else {
            setValue(action.fields[inputName].value);
        }
    }, [action]);

    const handleChange = (event) => {
        let v = event.target.value;
        if (!options || options?.checkValidation(v)) {
            setValue(v);
            action.fields[inputName].value = v;
        }
    };

    return (
        <TextField value={value}
            type={type} fullWidth
            onChange={handleChange}
            variant={variant}
        />
    );
}

export function TabPanel({ children, value, index }) {
    return (<div>{value === index && (<Box>{children}</Box>)}</div>);
}

export const StyledTabs = withStyles({
    root: {
        minHeight: 'min-content',
        width: 'min-content',
        textTransform: 'none',
        marginBottom: 5,
        marginTop: 4,
        borderRadius: 4,
        border: '1px solid rgba(0, 0, 0, 0.23)',
        backgroundColor: '#ffffffaa',
        '& button': {
            minHeight: 'min-content',
            textTransform: 'none',
            color: '#0B2949',
            minWidth: 100,
            fontSize: 12,
            zIndex: 1,
            fontWeight: 'bold',
            padding: '4px'
        }
    },
    indicator: {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        '& > span': {
            width: '50%',
            borderRadius: 3,
            backgroundColor: '#0B2949'
        }
    }
})((props) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

export const GreenCheckbox = withStyles({
    root: {
        color: 'gray',
        '&$checked': { color: green[600] }
    },
    checked: {}
})(props =>
    <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        color="default" {...props}
    />
);
